import * as React from 'react';

import {
  Money,
  PurchaseLineItemType,
  Unit,
  Weight,
} from '@rematter/frontend-lib-api-client';
import { WeightUnitType } from '@rematter/modules/trade/types/load';

export type InventoryItemRecord = {
  id: string;
  name: string;
  mnemonic?: string | null;
  itemGroupId?: string;
  categoryId?: string | null;
};

export type InventoryOption = {
  label: React.ReactNode;
  subLabel?: React.ReactNode;
  extraInfo?: React.ReactNode;
  group?: string;
  selectedLabel?: string;
  searchable?: string;
  key: string;
  isDisabled?: boolean;
};

export const enumLineItemKind = {
  LineItem: 'LineItem' as const,
  Deduction: 'Deduction' as const,
  FreightCharge: 'FreightCharge' as const,
};

export type LineItemKind = 'LineItem' | 'Deduction' | 'FreightCharge';

export type NormalizedLineItem = {
  kind: 'LineItem';
  id: string;
  type: PurchaseLineItemType;
  itemId: string | null;
  tagId: string | null;

  // gross, tare, net
  weights: Record<string, Weight | null> | null;

  unitPrice: Money | null;
  pricingUnit: Unit;

  notes?: string | null;

  sourceLineId?: string | null;

  parentLineItemId?: string | null;

  vehicleVin?: string | null;
  vehicleYear?: number | null;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  vehicleTitleStatus?: string | null;

  catSerialNumber?: string | null;
  catOemCode?: string | null;
  catComplianceStatus?: string | null;

  percentage?: number | null;

  tareOnTruck?: boolean | null;
};

/**
 * Normalized deduction representing a purchase_load_deduction.
 * Separate from NormalizedLineItem since deductions are a distinct entity.
 */
export type NormalizedDeduction = {
  kind: 'Deduction';
  id: string;
  deductionTypeId: string | null;
  deductionTypeName: string | null;
  applicationType: 'Weight' | 'Price';
  weightValue: Weight | null;
  priceValue: Money | null;
  calculatedAmount: Money | null;
  pricingUnit: Unit;
  percentage: number | null;
  purchaseLoadLineItemId?: string | null;
  loadLineItemId?: string | null;
  notes?: string | null;
};

export type NormalizedFreightCharge = {
  kind: 'FreightCharge';
  id: string;
  freightCost: Money | null;
  notes: string | null;
};

export type ItemsListEntry =
  | NormalizedLineItem
  | NormalizedDeduction
  | NormalizedFreightCharge;

export type ItemsInterfaceState = {
  items: ItemsListEntry[];
  heavyWeight: Weight | null;
  lightWeight: Weight | null;
};

export enum WeightField {
  Heavy = 'heavy',
  Light = 'light',
}

export type EntriesCallback = { fn: (entries: ItemsListEntry[]) => void };

export interface ItemFieldProps {
  lineItem: NormalizedLineItem;
  symbolDescription?: string;
  disabled?: boolean;
  weightUnit?: WeightUnitType;
  facilityDefaultUnit?: Unit;
  onUpdateItem: (id: string, updates: Partial<NormalizedLineItem>) => void;
  onDeleteItem: (id: string) => void;
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

export interface DeductionFieldProps {
  deduction: NormalizedDeduction;
  disabled?: boolean;
  weightUnit?: WeightUnitType;
  facilityDefaultUnit?: Unit;
  onUpdateDeduction: (
    id: string,
    updates: Partial<NormalizedDeduction>,
  ) => void;
  onDeleteDeduction: (id: string) => void;
}

export interface FreightChargeFieldProps {
  freightCharge: NormalizedFreightCharge;
  disabled?: boolean;
  onUpdateFreightCharge: (
    id: string,
    updates: Partial<NormalizedFreightCharge>,
  ) => void;
  onDeleteFreightCharge: (id: string) => void;
}

export type LineItemAdapter<T, TType extends string = PurchaseLineItemType> = {
  toNormalized: (item: T) => NormalizedLineItem;
  fromNormalized: (item: NormalizedLineItem, original?: T) => T;
  createEmpty: (type: TType, partial?: Partial<NormalizedLineItem>) => T;
};
